import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../api/axios";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { PMField } from "../../../components/utils/PMField";
import { DrawingStructureTree } from "../../../components/dashboard/drawings/drawing-structure-tree";
import { projectFromSelectorEvent } from "../../../components/dashboard/drawings/drawingStructureState";
import { DrawingPdfUploadField } from "../../../components/dashboard/drawings/drawing-pdf-upload-field";
import {
  DEFAULT_DRAWING_KIND,
  DRAWING_KINDS,
  drawingKindLabel,
} from "../../../components/dashboard/drawings/drawingKinds";
import { ConfirmDialog } from "../../../components/utils/Prompt";

const isValidDrawingId = (value) => {
  if (value == null || value === "") return false;
  const asString = String(value);
  return asString !== "undefined" && asString !== "null" && /^\d+$/.test(asString);
};

const normalizeDrawingKind = (kind) => {
  const normalized = typeof kind === "string" ? kind.trim() : kind;
  return normalized ? normalized : DEFAULT_DRAWING_KIND;
};

export const DrawingEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = isValidDrawingId(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [drawingFloorId, setDrawingFloorId] = useState(null);
  const [drawingKind, setDrawingKind] = useState(DEFAULT_DRAWING_KIND);
  const [selectedAndre, setSelectedAndre] = useState(!isEdit);
  const [replaceDialog, setReplaceDialog] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: null,
    document_id: null,
  });

  useEffect(() => {
    // If the route param looks like an edit route but the id is invalid, don't show create UI.
    if (id != null && String(id) !== "" && !isValidDrawingId(id)) {
      navigate("/dashboard/drawings", { replace: true });
      return;
    }
  }, [id]);

  // Create flow lives in the list right-drawer; keep /new as a deep-link.
  useEffect(() => {
    if (isEdit) return;
    const params = new URLSearchParams({ new: "1" });
    navigate(`/dashboard/drawings?${params.toString()}`, { replace: true });
  }, [isEdit, navigate]);

  useEffect(() => {
    if (isEdit) {
      loadDrawing();
    }
  }, [id]);

  const loadDrawing = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "get",
        url: `/drawings/${id}`,
      });
      const drawing = response.data;
      setFormData({
        title: drawing.title || "",
        description: drawing.description || "",
        project_id: drawing.project?.id || null,
        document_id: drawing.document?.id || null,
      });
      const floorId = drawing.drawing_floor?.id || drawing.drawing_floor_id || null;
      setDrawingFloorId(floorId);
      setSelectedAndre(!floorId);
      setDrawingKind(drawing.drawing_kind || DEFAULT_DRAWING_KIND);
      if (drawing.project) {
        setSelectedProject({
          id: drawing.project.id,
          displayTitle: drawing.project.displayTitle,
        });
      }
      if (drawing.document) {
        setUploadedDocument(drawing.document);
      }
    } catch (err) {
      console.error("Error loading drawing:", err);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  const findExistingDrawingForFloorAndKind = async (floorId, kind) => {
    if (!floorId) return null;
    const targetKind = normalizeDrawingKind(kind);
    const response = await axios({
      method: "get",
      url: "/drawings/",
      params: {
        drawing_floor_id: floorId,
        per_page: 5,
        page: 1,
        fields:
          "id,title,drawing_kind,floor,house,drawing_floor_id,drawing_floor,document",
      },
    });
    const rows = response.data?.data || [];
    return (
      rows.find((row) => {
        const rowKind = normalizeDrawingKind(row.drawing_kind);
        return (
          String(row.drawing_floor?.id || row.drawing_floor_id) === String(floorId) &&
          rowKind === targetKind
        );
      }) || null
    );
  };

  const buildPayload = () => ({
    drawing: {
      title: formData.title,
      description: formData.description,
      project_id: formData.project_id,
      drawing_floor_id: drawingFloorId || null,
      drawing_kind: drawingKind || DEFAULT_DRAWING_KIND,
      document_id: formData.document_id,
    },
  });

  const persistDrawing = async ({ targetId, replacedExisting }) => {
    const payload = buildPayload();
    let response;
    if (isValidDrawingId(targetId)) {
      response = await axios({
        method: "put",
        url: `/drawings/${targetId}`,
        data: payload,
      });
      toast.success(
        replacedExisting ? t("drawings.replaced") : t("drawings.updated")
      );
    } else {
      response = await axios({
        method: "post",
        url: "/drawings/",
        data: payload,
      });
      toast.success(t("drawings.created"));
    }

    const savedId = response.data?.id;
    if (!isValidDrawingId(savedId)) {
      console.error("Drawing save returned without id:", response.data);
      toast.error(
        isEdit || replacedExisting
          ? t("drawings.update_error")
          : t("drawings.create_error")
      );
      return;
    }

    navigate(`/dashboard/drawings/${savedId}`);
  };

  const openReplaceConfirm = (existing) => {
    const house =
      existing?.drawing_floor?.drawing_house?.name || existing?.house || "—";
    const floor = existing?.drawing_floor?.name || existing?.floor || "—";
    const kindLabel = drawingKindLabel(t, existing?.drawing_kind || drawingKind);
    setReplaceDialog({
      existing,
      message: t("drawings.replace_confirm.message", {
        title: existing?.title || `#${existing?.id}`,
        house,
        floor,
        kind: kindLabel,
      }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error(t("drawings.title.required"));
      return;
    }

    if (!formData.document_id) {
      toast.error(t("drawings.document.required"));
      return;
    }

    try {
      setSaving(true);

      // Create path: never silently replace — confirm first, then PUT.
      if (!isEdit && drawingFloorId) {
        const existing = await findExistingDrawingForFloorAndKind(
          drawingFloorId,
          drawingKind
        );
        if (isValidDrawingId(existing?.id)) {
          openReplaceConfirm(existing);
          return;
        }
      }

      // Edit path: if floor+kind moved onto another drawing, block.
      if (isEdit && drawingFloorId) {
        const existing = await findExistingDrawingForFloorAndKind(
          drawingFloorId,
          drawingKind
        );
        if (
          isValidDrawingId(existing?.id) &&
          String(existing.id) !== String(id)
        ) {
          toast.error(t("drawings.floor_kind_conflict"));
          return;
        }
      }

      await persistDrawing({
        targetId: isEdit ? id : null,
        replacedExisting: false,
      });
    } catch (err) {
      console.error("Error saving drawing:", err);
      if (err?.response?.status === 409 && err?.response?.data?.conflict) {
        if (!isEdit) {
          const existingDrawingId =
            err.response.data.conflict.existing_drawing_id;
          if (!isValidDrawingId(existingDrawingId)) {
            toast.error(t("drawings.floor_kind_conflict"));
            return;
          }
          openReplaceConfirm({
            id: existingDrawingId,
            title: err.response.data.conflict.title,
            house: err.response.data.conflict.house,
            floor: err.response.data.conflict.floor,
            drawing_kind: err.response.data.conflict.drawing_kind,
            drawing_floor: {
              id: err.response.data.conflict.drawing_floor_id,
              name: err.response.data.conflict.floor,
              drawing_house: { name: err.response.data.conflict.house },
            },
          });
          return;
        }
        toast.error(t("drawings.floor_kind_conflict"));
        return;
      }
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        toast.error(apiErrors.join(", "));
      } else {
        toast.error(
          isEdit ? t("drawings.update_error") : t("drawings.create_error")
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmReplace = async () => {
    const existing = replaceDialog?.existing;
    setReplaceDialog(null);
    if (!isValidDrawingId(existing?.id)) {
      toast.error(t("drawings.replace_confirm.invalid_target"));
      return;
    }

    try {
      setSaving(true);
      await persistDrawing({
        targetId: existing.id,
        replacedExisting: true,
      });
    } catch (err) {
      console.error("Error replacing drawing:", err);
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        toast.error(apiErrors.join(", "));
      } else {
        toast.error(t("drawings.update_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = (document) => {
    if (!document?.id) return;
    setUploadedDocument(document);
    setFormData((prev) => ({
      ...prev,
      document_id: document.id,
    }));
  };

  if (!isEdit) {
    return <ResourceLoading />;
  }

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={t("drawings.edit")}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <PMField
              name="title"
              type="text"
              label={t("drawings.title")}
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />

            <PMField
              name="description"
              type="textarea"
              label={t("drawings.description")}
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("drawings.kind")}
              </label>
              <div className="flex flex-wrap gap-2">
                {DRAWING_KINDS.map((kind) => {
                  const active = drawingKind === kind;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => {
                        setDrawingKind(kind);
                        setReplaceDialog(null);
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-semibold border ${
                        active
                          ? "border-blue-600 bg-blue-50 text-blue-800"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {drawingKindLabel(t, kind)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">{t("drawings.kind_hint")}</p>
            </div>

            <div>
              <ProjectSelector
                name="project_id"
                selected={selectedProject}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const project = projectFromSelectorEvent(e);
                  setSelectedProject(project);
                  setFormData((prev) => ({ ...prev, project_id: projectId || null }));
                  setDrawingFloorId(null);
                  setSelectedAndre(true);
                }}
                label={t("drawings.project")}
              />
            </div>

            {formData.project_id ? (
              <div className="rounded-lg border border-gray-200 p-4">
                <DrawingStructureTree
                  t={t}
                  projectId={formData.project_id}
                  selectedFloorId={drawingFloorId}
                  selectedAndre={selectedAndre}
                  onSelectFloor={(floorId) => {
                    setDrawingFloorId(floorId);
                    setSelectedAndre(floorId == null);
                    setReplaceDialog(null);
                  }}
                  onSelectAndre={() => {
                    setDrawingFloorId(null);
                    setSelectedAndre(true);
                    setReplaceDialog(null);
                  }}
                />
                <p className="mt-3 text-xs text-gray-500">
                  {drawingFloorId
                    ? t("drawings.floor_kind_hint")
                    : t("drawings.andre_hint")}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("drawings.structure.project_required")}</p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("drawings.document")}
                {!uploadedDocument && <span className="text-red-500 ml-1">*</span>}
              </label>
              {uploadedDocument ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {uploadedDocument.title || t("drawings.document")}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedDocument(null);
                      setFormData((prev) => ({ ...prev, document_id: null }));
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    {t("drawings.document.replace")}
                  </button>
                </div>
              ) : (
                <DrawingPdfUploadField
                  t={t}
                  title={formData.title}
                  disabled={saving}
                  onUploaded={handleDocumentUpload}
                />
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? t("Saving...") : (isEdit ? t("Update") : t("Create"))}
              </button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={Boolean(replaceDialog)}
        onClose={() => setReplaceDialog(null)}
        onConfirm={handleConfirmReplace}
        title={t("drawings.replace_confirm.title")}
        message={replaceDialog?.message || ""}
        confirmText={t("drawings.replace_confirm.confirm")}
        cancelText={t("drawings.replace_confirm.cancel")}
      />
    </div>
  );
};
